import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureListing, ensureUser } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { useConfiguration } from '../../context/configurationContext';

import { NamedLink } from '../../components';

import css from './ListingCard.module.css';

const COMPANION_VALS = ['other', 'us_state', 'australian_state'];

// Resolve a raw enum key (or array of keys) to human-readable label(s).
// Falls back to the companion free-text field (other_<fieldKey>) for special values.
const resolveLabels = (value, fieldKey, enumOptions, publicData) => {
  if (!value) return [];
  const values = Array.isArray(value) ? value : [value];
  return values.map(v => {
    if (COMPANION_VALS.includes(v)) return publicData?.[`other_${fieldKey}`] || null;
    return enumOptions?.find(o => o.option === v)?.label || v;
  }).filter(Boolean);
};

const MAX_DESCRIPTION_LENGTH = 80;

const truncateDescription = text => {
  if (!text) return null;
  return text.length > MAX_DESCRIPTION_LENGTH
    ? text.substring(0, MAX_DESCRIPTION_LENGTH).trim() + '…'
    : text;
};

export const ListingCard = props => {
  const {
    className,
    rootClassName,
    listing,
    setActiveListing,
    showAuthorInfo = true,
  } = props;

  const config = useConfiguration();
  const allFields = [
    ...(config?.listing?.listingFields || []),
    ...(config?.user?.userFields || []),
  ];
  const getOptions = fieldKey => allFields.find(f => f.key === fieldKey)?.enumOptions;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', description, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const sectorLabels = resolveLabels(publicData?.sector, 'sector', getOptions('sector'), publicData);
  const specialismLabels = resolveLabels(publicData?.specialism, 'specialism', getOptions('specialism'), publicData);
  const jurisdictionLabels = resolveLabels(publicData?.jurisdiction, 'jurisdiction', getOptions('jurisdiction'), publicData);

  // Badge shows the first sector label; falls back to nothing if absent
  const badgeLabel = sectorLabels.length > 0 ? sectorLabels[0] : null;

  const avatarLetter = title ? title.charAt(0).toUpperCase() : '?';

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : {};

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }} {...setActivePropsMaybe}>
      {/* Sector badge (top-right) */}
      {badgeLabel ? (
        <span className={css.sectorBadge}>{badgeLabel}</span>
      ) : null}

      {/* Avatar */}
      <div className={css.avatar}>
        <span className={css.avatarLetter}>{avatarLetter}</span>
      </div>

      {/* Title */}
      <div className={css.title}>{title}</div>

      {/* Author */}
      {showAuthorInfo && authorName ? (
        <div className={css.authorInfo}>
          <FormattedMessage id="ListingCard.author" values={{ authorName }} />
        </div>
      ) : null}

      {/* Description — max 100 chars / 2 lines */}
      {description ? (
        <p className={css.description}>{truncateDescription(description)}</p>
      ) : (
        <p className={css.descriptionPlaceholder}>No description provided.</p>
      )}

      {/* Meta fields — one row, label top / badge bottom */}
      {(jurisdictionLabels.length > 0 || specialismLabels.length > 0) ? (
        <div className={css.metaSection}>
          {jurisdictionLabels.length > 0 ? (
            <div className={css.metaCol}>
              <span className={css.metaLabel}>Jurisdiction</span>
              <span className={css.badgeBlack}>{jurisdictionLabels[0]}</span>
            </div>
          ) : null}
          {specialismLabels.length > 0 ? (
            <div className={css.metaCol}>
              <span className={css.metaLabel}>Specialism</span>
              <div className={css.badgeRow}>
                {specialismLabels.slice(0, 2).map((s, i) => (
                  <span key={i} className={css.badgeBlue}>{s}</span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </NamedLink>
  );
};

export default ListingCard;
